import React, { useState } from 'react';
import { Clipboard, Calendar, Clock, Terminal, Globe, Check, RotateCcw, ArrowDownUp } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';

/**
 * Компонент отображения результатов конвертации Unix-времени
 */
const ResultsDisplay = ({ results, formData }) => {
  const [copiedField, setCopiedField] = useState(null);

  if (!results) return null;

  // Обработчик копирования в буфер обмена
  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => {
        setCopiedField(null);
      }, 2000);
    });
  };

  // Форматирование даты для отображения
  const formatDate = (date, format) => {
    if (!date) return '';

    // Для формата "день недели, дата время"
    if (format === 'full') {
      const options = { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      };
      return new Date(date).toLocaleString('ru-RU', options);
    }

    // Для краткого формата даты
    if (format === 'short') {
      const options = { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      };
      return new Date(date).toLocaleString('ru-RU', options);
    }

    return date;
  };

  return (
    <ToolResult title="Результат конвертации">
      {/* Ключевые метрики: основной результат в зависимости от типа конвертации */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {formData.conversionType === 'unix-to-human' ? (
          <>
            {/* Результат конвертации из Unix в человекочитаемую дату */}
            <div className="rounded-lg bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/30 dark:to-blue-900/30 p-5 border border-cyan-100 dark:border-cyan-800/50 shadow-sm">
              <h3 className="text-base font-medium text-cyan-700 dark:text-cyan-300 mb-1.5 flex items-center gap-1.5">
                <Calendar className="w-4 h-4"/>
                Дата и время
              </h3>
              <div className="flex items-baseline flex-wrap gap-x-2">
                <span className="text-2xl font-bold text-cyan-800 dark:text-cyan-200" aria-live="polite">
                  {formatDate(results.humanDate, 'short')}
                </span>
                <button 
                  onClick={() => copyToClipboard(formatDate(results.humanDate, 'short'), 'humanDate')}
                  className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-300 transition-colors"
                  title="Копировать"
                >
                  {copiedField === 'humanDate' ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Clipboard className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-cyan-600 dark:text-cyan-400 mt-1">
                Часовой пояс: {formData.timezone.replace('_', ' ')}
              </p>
            </div>

            {/* Исходный Unix timestamp */}
            <div className="rounded-lg bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-slate-800/50 p-5 border border-gray-200 dark:border-gray-700/50 shadow-sm">
              <h3 className="text-base font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center gap-1.5">
                <Terminal className="w-4 h-4"/>
                Unix Timestamp
              </h3>
              <div className="flex items-baseline flex-wrap gap-x-2">
                <span className="text-2xl font-mono font-bold text-gray-800 dark:text-gray-200" aria-live="polite">
                  {results.unixTimestamp}
                </span>
                <button 
                  onClick={() => copyToClipboard(results.unixTimestamp, 'unixTimestamp')}
                  className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
                  title="Копировать"
                >
                  {copiedField === 'unixTimestamp' ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Clipboard className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {formData.includeMilliseconds ? 'Миллисекунды с 1 января 1970' : 'Секунды с 1 января 1970'}
              </p>
            </div>
          </>
        ) : (
          <>
            {/* Результат конвертации из даты в Unix timestamp */}
            <div className="rounded-lg bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/30 dark:to-blue-900/30 p-5 border border-cyan-100 dark:border-cyan-800/50 shadow-sm">
              <h3 className="text-base font-medium text-cyan-700 dark:text-cyan-300 mb-1.5 flex items-center gap-1.5">
                <Terminal className="w-4 h-4"/>
                Unix Timestamp
              </h3>
              <div className="flex items-baseline flex-wrap gap-x-2">
                <span className="text-2xl font-mono font-bold text-cyan-800 dark:text-cyan-200" aria-live="polite">
                  {results.unixTimestamp}
                </span>
                <button 
                  onClick={() => copyToClipboard(results.unixTimestamp, 'unixTimestamp')}
                  className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-300 transition-colors"
                  title="Копировать"
                >
                  {copiedField === 'unixTimestamp' ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Clipboard className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-cyan-600 dark:text-cyan-400 mt-1">
                {formData.includeMilliseconds ? 'Миллисекунды с 1 января 1970' : 'Секунды с 1 января 1970'}
              </p>
            </div>

            {/* Исходная человекочитаемая дата */}
            <div className="rounded-lg bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-slate-800/50 p-5 border border-gray-200 dark:border-gray-700/50 shadow-sm">
              <h3 className="text-base font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center gap-1.5">
                <Calendar className="w-4 h-4"/>
                Исходная дата
              </h3>
              <div className="flex items-baseline flex-wrap gap-x-2">
                <span className="text-2xl font-bold text-gray-800 dark:text-gray-200" aria-live="polite">
                  {formatDate(results.humanDate, 'short')}
                </span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Часовой пояс: {formData.timezone.replace('_', ' ')}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Дополнительные форматы даты */}
      {formData.conversionType === 'unix-to-human' && (
        <div className="space-y-3 mb-6">
          <h3 className="text-md font-medium flex items-center gap-2">
            <Clock className="w-4 h-4 text-cyan-500" />
            Альтернативные форматы даты и времени
          </h3>

          {formData.showISO && (
            <ResultItem 
              label="ISO 8601 (международный стандарт)" 
              value={
                <div className="flex items-center gap-2">
                  <code className="font-mono text-xs bg-cyan-50 dark:bg-cyan-900/20 p-1 rounded">
                    {results.humanDateISO}
                  </code>
                  <button 
                    onClick={() => copyToClipboard(results.humanDateISO, 'iso')}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    title="Копировать"
                  >
                    {copiedField === 'iso' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Clipboard className="w-4 h-4" />
                    )}
                  </button>
                </div>
              } 
              color="cyan" 
            />
          )}
          
          {formData.showRFC && (
            <ResultItem 
              label="RFC 2822 (для Email, HTTP)" 
              value={
                <div className="flex items-center gap-2">
                  <code className="font-mono text-xs bg-cyan-50 dark:bg-cyan-900/20 p-1 rounded">
                    {results.humanDateRFC}
                  </code>
                  <button 
                    onClick={() => copyToClipboard(results.humanDateRFC, 'rfc')}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    title="Копировать"
                  >
                    {copiedField === 'rfc' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Clipboard className="w-4 h-4" />
                    )}
                  </button>
                </div>
              } 
              color="cyan" 
            />
          )}
          
          {formData.showLocale && (
            <ResultItem 
              label="Локализованный формат (Россия)" 
              value={
                <div className="flex items-center gap-2">
                  <span className="text-md">
                    {results.humanDateLocale}
                  </span>
                  <button 
                    onClick={() => copyToClipboard(results.humanDateLocale, 'locale')}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    title="Копировать"
                  >
                    {copiedField === 'locale' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Clipboard className="w-4 h-4" />
                    )}
                  </button>
                </div>
              } 
              color="cyan" 
              icon={<Globe className="w-4 h-4"/>} 
            />
          )}

          {formData.showRelative && (
            <ResultItem 
              label="Относительное время" 
              value={results.relativeTime} 
              color="cyan" 
              icon={<RotateCcw className="w-4 h-4"/>} 
            />
          )}

          <ResultItem 
            label="Полный формат даты и времени" 
            value={formatDate(results.humanDate, 'full')} 
            color="cyan" 
          />
        </div>
      )}

      {/* Информация об использовании в коде */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-4 border border-gray-200 dark:border-gray-700">
        <h3 className="text-md font-medium mb-2 flex items-center gap-2">
          <Terminal className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          Использование в коде
        </h3>
        
        <div className="space-y-3">
          <div className="bg-gray-100 dark:bg-gray-700 rounded p-3">
            <div className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-1">JavaScript:</div>
            <code className="text-xs font-mono text-gray-800 dark:text-gray-200 block overflow-x-auto whitespace-pre pb-1">
              {formData.conversionType === 'unix-to-human' ? 
                `// Конвертация Unix-времени в объект Date
const timestamp = ${results.unixTimestamp}${!formData.includeMilliseconds ? ' * 1000' : ''};
const date = new Date(timestamp);
console.log(date.toLocaleString('ru-RU')); // ${results.humanDateLocale}` :
                `// Конвертация даты в Unix-время
const date = new Date("${results.humanDateISO}");
const unixTime = ${formData.includeMilliseconds ? 'date.getTime()' : 'Math.floor(date.getTime() / 1000)'};
console.log(unixTime); // ${results.unixTimestamp}`
              }
            </code>
          </div>
          <div className="bg-gray-100 dark:bg-gray-700 rounded p-3">
            <div className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-1">Python:</div>
            <code className="text-xs font-mono text-gray-800 dark:text-gray-200 block overflow-x-auto whitespace-pre pb-1">
              {formData.conversionType === 'unix-to-human' ? 
                `# Конвертация Unix-времени в дату
import datetime

timestamp = ${results.unixTimestamp}${!formData.includeMilliseconds ? ' # секунды' : ' / 1000 # миллисекунды в секунды'}
date = datetime.datetime.fromtimestamp(timestamp)
print(date.strftime("%d.%m.%Y %H:%M:%S")) # примерно: ${results.humanDateLocale}` :
                `# Конвертация даты в Unix-время
import datetime
import time

date_string = "${results.humanDateISO}"
date_obj = datetime.datetime.fromisoformat(date_string.replace('Z', '+00:00'))
unix_time = ${formData.includeMilliseconds ? 'int(date_obj.timestamp() * 1000)  # миллисекунды' : 'int(date_obj.timestamp())  # секунды'}
print(unix_time) # ${results.unixTimestamp}`
              }
            </code>
          </div>
          <div className="bg-gray-100 dark:bg-gray-700 rounded p-3">
            <div className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-1">PHP:</div>
            <code className="text-xs font-mono text-gray-800 dark:text-gray-200 block overflow-x-auto whitespace-pre pb-1">
              {formData.conversionType === 'unix-to-human' ? 
                `// Конвертация Unix-времени в дату
$timestamp = ${results.unixTimestamp}${!formData.includeMilliseconds ? '' : ' / 1000'};
$date = new DateTime();
$date->setTimestamp($timestamp);
echo $date->format('d.m.Y H:i:s'); // примерно: ${results.humanDateLocale}` :
                `// Конвертация даты в Unix-время
$date = new DateTime("${results.humanDateISO}");
$unixTime = ${formData.includeMilliseconds ? '$date->getTimestamp() * 1000' : '$date->getTimestamp()'};
echo $unixTime; // ${results.unixTimestamp}`
              }
            </code>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsDisplay;